<template>
  <div class="main-container">
    <!-- ───────── HEADER ───────── -->
    <v-row class="pa-2 header-row">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <!-- Back -->
        <v-btn icon class="back-button my-4 mx-4" @click="confirmBackNavigation">
          <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
        </v-btn>

        <!-- Add Delivery (if allowed) -->
        <v-btn
          v-if="hasDeliveryEditPermission"
          color="#AC1726"
          width="150"
          dense
          class="add-delivery-button mx-4"
          dark
          @click="navigateToAddDelivery"
        >
          Add Delivery
        </v-btn>
      </v-col>
    </v-row>

    <!-- ───────── LOADER ───────── -->
    <v-row v-if="loading" justify="center" align="center" class="loader-row">
      <v-progress-circular indeterminate color="#AC1726" />
    </v-row>

    <!-- ───────── MAIN LIST ───────── -->
    <div v-if="!loading" class="deliveries-container">
      <!-- Search & Status Filter -->
      <v-row justify="center" class="search-filter-row">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="pa-3 white-search-field"
            filled dense rounded
            label="Search Deliveries"
            single-line hide-details
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="selectedStatus"
            :items="statusOptionsWithClear"
            placeholder="Filter by Status"
            filled dense rounded
            @change="handleStatusChange"
          />
        </v-col>
      </v-row>

      <!-- Cards -->
      <v-row>
        <v-col
          v-for="(delivery, index) in paginatedDeliveries"
          :key="index"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-card
            class="modern-card"
            elevation="6"
            hover
            @click="handleCardClick(delivery)"
          >
            <!-- Card header: urgency square, status chip, number chip, copy icon -->
            <v-row class="d-flex justify-end pr-3 pt-3">
              <div :class="['urgency-square', getUrgencySquareColor(delivery.urgency)]" />
              <v-chip
                :color="getChipColor(delivery.status)"
                dark label
                class="status-chip ml-2"
              >
                {{ delivery.status || 'Unknown' }}
              </v-chip>
              <v-chip color="grey" dark label class="delivery-number-chip ml-2">
                No.{{ delivery.deliveryNumber || 'Unknown' }}
              </v-chip>
              <v-btn icon class="ml-2" title="Copy Card Content" @click.stop="copyCardContent(delivery)">
                <v-icon color="#AC1726">mdi-content-copy</v-icon>
              </v-btn>
            </v-row>

            <!-- Card body -->
            <v-card-text class="pa-4">
              <v-row justify="end">
                <v-col cols="12" class="py-2 text-right">
                  <span class="text-caption">Date:</span>
                  <span class="font-weight-medium">{{ formatDate(delivery.date) }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2">
                  <span class="text-caption">Priority:</span>
                  <span class="font-weight-medium">{{ delivery.urgency || 'Unknown' }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2">
                  <span class="text-caption">Driver Name:</span>
                  <span class="font-weight-medium">{{ delivery.driverName || 'N/A' }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2">
                  <span class="text-caption">Location:</span>
                  <span class="font-weight-medium">{{ delivery.location || 'Unknown' }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2">
                  <span class="text-caption">Method:</span>
                  <span class="font-weight-medium">{{ delivery.method || 'Unknown' }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2">
                  <span class="text-caption">Type:</span>
                  <span class="font-weight-medium">{{ delivery.type || 'N/A' }}</span>
                </v-col>
              </v-row>
            </v-card-text>

            <!-- “Open Delivery” (if allowed) -->
            <v-card-actions v-if="hasDeliveryEditPermission">
              <v-btn
                block rounded dark
                color="#AC1726"
                class="open-delivery-button"
                @click.stop="handleCardClick(delivery)"
              >
                Open Delivery
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Load-More -->
      <v-row justify="center" v-if="canLoadMore">
        <v-btn
          color="#AC1726"
          class="load-more-button"
          :disabled="!canLoadMore"
          @click="loadMore"
        >
          Load More
        </v-btn>
      </v-row>
    </div>

    <!-- Back confirm -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="#AC1726" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="#28a745" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db   = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: "DeliveriesComponent",
  data() {
    return {
      deliveries: [],          // fetched list
      search: "",
      selectedStatus: "",      // status filter
      statusOptions: ["In Queue", "Booked", "In Transit", "Delayed", "Complete"],

      // user / perms
      userRole: "",
      userPermissions: {},

      // ui
      loading: true,
      backDialog: false,
      visibleCount: 16,
      loadIncrement: 16,

      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success"
    };
  },

  computed: {
    statusOptionsWithClear() {
      return ["Clear filter", ...this.statusOptions];
    },

    hasDeliveryEditPermission() {
      // show edit controls only if *neither* Add nor Edit flag is set
      return !(this.userPermissions.Add_Button || this.userPermissions.Edit_Details);
    },

    filteredDeliveries() {
      let arr = this.deliveries;

      // search text
      if (this.search) {
        const s = this.search.toLowerCase();
        arr = arr.filter(d =>
          String(d.deliveryNumber).toLowerCase().includes(s) ||
          (d.method      && d.method.toLowerCase().includes(s)) ||
          (d.driverName  && d.driverName.toLowerCase().includes(s)) ||
          (d.location    && d.location.toLowerCase().includes(s)) ||
          (d.urgency     && d.urgency.toLowerCase().includes(s)) ||
          (d.status      && d.status.toLowerCase().includes(s))
        );
      }

      // status filter
      if (this.selectedStatus && this.selectedStatus !== "Clear filter") {
        arr = arr.filter(d => d.status === this.selectedStatus);
      }

      // newest first by deliveryNumber
      return arr.sort((a, b) => (+b.deliveryNumber || 0) - (+a.deliveryNumber || 0));
    },

    paginatedDeliveries() { return this.filteredDeliveries.slice(0, this.visibleCount); },
    canLoadMore()        { return this.visibleCount < this.filteredDeliveries.length; }
  },

  watch: {
    search()         { this.resetPagination(); },
    selectedStatus() { this.resetPagination(); }
  },

  async created() {
    await this.fetchUserRole();
    await this.fetchDeliveries();
    this.loading = false;
  },

  methods: {
    /* ───────── USER & PERMS ───────── */
    async fetchUserRole() {
      await new Promise(resolve => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
              const d = snap.data();
              this.userRole        = d.role || "";
              this.userPermissions = d.deliveries || {};
            }
          }
          resolve();
        });
      });
    },

    /* ───────── DATA ───────── */
    async fetchDeliveries() {
      try {
        const snap = await getDocs(collection(db, "deliveries"));
        const arr  = [];
        snap.forEach(docSnap => {
          const d = docSnap.data();
          arr.push({
            id: docSnap.id,
            deliveryNumber: d.deliveryNumber,
            method:         d.method,
            location:       d.location      || "Unknown",
            driverName:     d.driverName    || "N/A",
            status:         d.status        || "Unknown",
            items:          d.items         || [],
            type:           d.type          || "",
            urgency:        d.urgency       || "Unknown",
            date:           d.date          || "Unknown"
          });
        });
        this.deliveries = arr;
      } catch (e) {
        console.error("Fetch deliveries error:", e);
        this.showSnackbar("Error fetching deliveries", "error");
      }
    },

    /* ───────── UI HELPERS ───────── */
    handleStatusChange(val) {
      if (val === "Clear filter") this.clearFilters();
    },
    clearFilters()      { this.selectedStatus = ""; this.search = ""; },
    loadMore()          { this.visibleCount += this.loadIncrement; },
    resetPagination()   { this.visibleCount  = this.loadIncrement; },

    confirmBackNavigation() { this.backDialog = true; },
    goBack() { this.backDialog = false; this.$router.go(-1); },

    handleCardClick(d) {
      if (!d.id) return;
      localStorage.setItem("selectedDelivery", d.id);
      this.$router.push({ name: "EditDeliveries" });
    },
    navigateToAddDelivery() { this.$router.push({ path: "/addDeliveries" }); },

    getUrgencySquareColor(u) {
      const map = { Urgent: "red-square", Medium: "orange-square", "Not urgent": "green-square" };
      return map[u] || "grey-square";
    },

    getChipColor(s) {
      const m = {
        "in queue": "blue",
        booked: "teal",
        "in transit": "purple",
        delayed: "red",
        complete: "green",
        completed: "green",
        pending: "orange"
      };
      return m[s?.toLowerCase()] || "grey";
    },
    formatDate(s) {
      if (!s || s === "Unknown") return "Unknown";
      const d = new Date(s);
      return isNaN(d) ? s : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    },

    copyCardContent(d) {
      const txt = `Delivery Number: ${d.deliveryNumber}
Date: ${this.formatDate(d.date)}
Priority: ${d.urgency}
Driver Name: ${d.driverName}
Location: ${d.location}
Method: ${d.method}
Type: ${d.type}
Status: ${d.status}`;
      navigator.clipboard.writeText(txt)
        .then(() => this.showSnackbar("Card content copied"))
        .catch(() => this.showSnackbar("Copy failed", "error"));
    },

    showSnackbar(msg, color = "success") {
      this.snackbarMessage = msg;
      this.snackbarColor   = color;
      this.snackbar        = true;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

body, div, span, v-card-title, v-card-text, v-row, v-col, v-card, v-chip {
  font-family: 'Montserrat', sans-serif;
}

.main-container      { background:#121212; min-height:100vh; }
.header-row          { background:#1e1e1e; border-bottom:1px solid #333; }
.deliveries-container{ background:#fff; border-radius:20px 20px 0 0; padding:24px; box-shadow:0 4px 15px rgba(0,0,0,.1); }
.search-filter-row   { margin-bottom:24px; }

.modern-card { background:#fff; border-radius:16px; overflow:hidden; transition:.3s; cursor:pointer; margin:8px;
  box-shadow:0 4px 15px rgba(0,0,0,.1); }
.modern-card:hover { transform:translateY(-5px); box-shadow:0 8px 25px rgba(0,0,0,.2); }

.urgency-square     { width:20px; height:20px; border-radius:4px; }
.red-square         { background:red; }
.orange-square      { background:orange; }
.green-square       { background:green; }
.grey-square        { background:grey; }

.status-chip        { font-size:.75rem; text-transform:capitalize; }
.delivery-number-chip{ font-size:.75rem; text-transform:uppercase; }

.open-delivery-button{ font-weight:600; transition:.3s; }
.open-delivery-button:hover{ background:#e63946; transform:scale(1.02); }

.back-button        { color:#fff; border-radius:50%; transition:.3s; }
.back-button:hover  { transform:scale(1.1); }

.add-delivery-button,
.load-more-button   { font-weight:700; transition:.3s; }
.add-delivery-button:hover,
.load-more-button:hover { background:#e63946; transform:scale(1.02); }

.white-search-field .v-input__control{ background:#fff; border-radius:8px; }
.white-search-field .v-label{ color:#555; }
.white-search-field .v-input__icon--append .v-icon{ color:#555; }

.loader-row         { margin-top:50px; }
.v-dialog .v-card   { border-radius:16px; }
</style>
